/**
 * Typed fetch wrapper for our own /api/* routes.
 *
 * Keeps API call sites typed (request + response) and centralizes
 * error handling. All callers should go through these helpers — never
 * fetch('/api/...') directly from components.
 */

import type {
  AvailabilityRequest,
  AvailabilityResponse,
  BookRequest,
  BookResponse,
  BookingByTokenResponse,
  CancelBookingRequest,
  CancelBookingResponse,
  HealthResponse,
  QualifyRequest,
  QualifyResponse,
  RescheduleBookingRequest,
  RescheduleBookingResponse,
} from './types';
import { supabase } from '../supabase/client';

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

async function postJson<TReq, TRes>(
  path: string,
  body: TReq,
  signal?: AbortSignal,
  extraHeaders?: Record<string, string>
): Promise<TRes> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(extraHeaders ?? {}) },
    body: JSON.stringify(body),
    signal,
  });

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    throw new ApiError(res.status, `Bad JSON from ${path} (status ${res.status})`);
  }

  if (!res.ok && (payload as { ok?: boolean })?.ok !== false) {
    throw new ApiError(res.status, `${path} returned ${res.status}`);
  }
  return payload as TRes;
}

async function getJson<TRes>(
  path: string,
  signal?: AbortSignal
): Promise<TRes> {
  const res = await fetch(path, { signal });
  if (!res.ok) {
    throw new ApiError(res.status, `${path} returned ${res.status}`);
  }
  return (await res.json()) as TRes;
}

// -----------------------------------------------------------------------------
// Endpoints
// -----------------------------------------------------------------------------

export function getHealth(signal?: AbortSignal): Promise<HealthResponse> {
  return getJson<HealthResponse>('/api/health', signal);
}

export function postQualify(
  body: QualifyRequest,
  signal?: AbortSignal
): Promise<QualifyResponse> {
  return postJson<QualifyRequest, QualifyResponse>('/api/qualify', body, signal);
}

export function postAvailability(
  body: AvailabilityRequest,
  signal?: AbortSignal
): Promise<AvailabilityResponse> {
  return postJson<AvailabilityRequest, AvailabilityResponse>(
    '/api/availability',
    body,
    signal
  );
}

export async function postBook(
  body: BookRequest,
  signal?: AbortSignal
): Promise<BookResponse> {
  // Booker attribution: if a staff session exists (advisor or
  // appointment setter booking from the workspace, book-on-behalf, or
  // their own personal link while signed in), attach the bearer so
  // /api/book stamps booked_by_user_id and the Appointment FYI shows
  // "Booked by <name>". Prospects never have a session — their request
  // stays anonymous and the FYI falls back to self-booked / UTM source.
  // Best-effort: a session-read hiccup must never block a booking.
  let extraHeaders: Record<string, string> | undefined;
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) extraHeaders = { Authorization: `Bearer ${token}` };
  } catch {
    // anonymous booking
  }
  return postJson<BookRequest, BookResponse>('/api/book', body, signal, extraHeaders);
}

export function getBookingByToken(
  token: string,
  signal?: AbortSignal
): Promise<BookingByTokenResponse> {
  const url = `/api/booking-by-token?token=${encodeURIComponent(token)}`;
  return getJson<BookingByTokenResponse>(url, signal);
}

export function postCancelBooking(
  body: CancelBookingRequest,
  signal?: AbortSignal
): Promise<CancelBookingResponse> {
  return postJson<CancelBookingRequest, CancelBookingResponse>(
    '/api/cancel-booking',
    body,
    signal
  );
}

export function postRescheduleBooking(
  body: RescheduleBookingRequest,
  signal?: AbortSignal
): Promise<RescheduleBookingResponse> {
  return postJson<RescheduleBookingRequest, RescheduleBookingResponse>(
    '/api/reschedule-booking',
    body,
    signal
  );
}

export { ApiError };
